import { computed, reactive } from 'vue';
import axios from 'axios';
import { captureUnlessPageLeft, Sentry } from './sentry.js';

// The basket's contents and the calls that change them. Plain reactive singleton shared
// by ES module import, same convention as modeStore.js / searchBarStore.js /
// panelStack.js - every Vue island on the page gets the same instance.
//
// Was sidebarStore.js, which also held which view the sidebar was showing. That view
// state is gone: panels are now a stack (shared/panelStack.js) rather than one sidebar
// swapping its contents, and a store that owns basket data has no business also owning
// what is on screen. See docs/blade-to-vue-components.md.
export const state = reactive({
    basket: { items: [], vouchers: [], total: 0, discount: 0, payable: 0 },

    // When this basket's Phorest hold runs out, as a client-clock timestamp in ms, or null
    // when nothing is held. Set by setBasket() - see its comment for why it has to exist.
    basketExpiresAtMs: null,

    basketError: null,
    // What the last change did as a side effect, as opposed to why it failed - today only
    // "your discount code no longer applies", sent back by the update endpoint. Separate from
    // basketError because the change DID happen: showing it as an error would read as though
    // the edit had been refused.
    basketNotice: null,
});

// What the vouchers on the basket are worth, summed, or null when there are none so
// callers can withhold the whole label rather than render "£0".
//
// Sums `balance`, which BasketController@voucherPayload (see its docblock) documents as
// each voucher's own remaining value and deliberately NOT what comes off this order -
// that figure is `discount`, and the two differ whenever the basket is worth less than
// the vouchers on it. The customer's credit is the right one to show in the header: it
// is what they are carrying, and it holds still as the basket fills, where `discount`
// would climb from zero and read as though the amount kept changing.
//
// Coerced per voucher because `balance` is null for a voucher with no value (a product
// voucher covering a fixed item), and JSON may carry the figure as a string.
export const voucherCredit = computed(() => {
    const total = state.basket.vouchers.reduce(
        (sum, voucher) => sum + (Number(voucher.balance) || 0),
        0,
    );

    return total > 0 ? total : null;
});

let basketApiUrl = null;
let basketGiftApiUrl = null;
let basketMonetaryVoucherApiUrl = null;
let basketVoucherApiUrl = null;
let basketCheckoutUrl = null;

// Only the keys actually passed are assigned, so a caller that needs one url does not blank
// the rest. The checkout page is why: its header is the stripped-back one with no basket
// button (partials/header.blade.php), so nothing there calls this with the full set - but the
// summary column's edit controls still need the basket url to PATCH an item and to fetch a
// booking panel. See docs/feature-requests/checkout-location-date-editing-scope-technical.md §9.
export function configureBasket({ apiUrl, giftApiUrl, monetaryVoucherApiUrl, voucherApiUrl, checkoutUrl }) {
    if (apiUrl !== undefined) basketApiUrl = apiUrl;
    if (giftApiUrl !== undefined) basketGiftApiUrl = giftApiUrl;
    if (monetaryVoucherApiUrl !== undefined) basketMonetaryVoucherApiUrl = monetaryVoucherApiUrl;
    if (voucherApiUrl !== undefined) basketVoucherApiUrl = voucherApiUrl;
    if (checkoutUrl !== undefined) basketCheckoutUrl = checkoutUrl;
}

// False rather than a throw when nothing configured the store - the monetary voucher form
// is a CMS section that can be placed on a page with no basket button to configure it, and
// a page that cannot reach the basket should say so, not break.
export function isBasketConfigured() {
    return basketApiUrl !== null;
}

export function getBasketCheckoutUrl() {
    return basketCheckoutUrl;
}

// The one way to put a payload on the store. Exists because of the hold countdown: Phorest's
// hold reaches us as seconds remaining rather than an absolute time (BasketController's
// basketResponse()), so that figure is only true at the instant it lands. Anchoring it
// against the clock here, once, lets anything work out the real remaining time whenever it
// asks - including a basket panel mounting fresh minutes later, which is what closing and
// reopening the panel now does.
//
// Relative seconds is still the right thing for the server to send, and this does not change
// it: a duration cannot be thrown off by a customer's clock being wrong, where a timestamp
// could. It only needs anchoring on arrival rather than on whenever something first renders it.
//
// Kept beside state.basket rather than inside it, because everything in there came from the
// server and this did not.
//
// Takes the response, not its data: the shape check reports the status and content type.
function setBasket(response) {
    const { data } = response;

    // Axios only rejects on a non-2xx status, so a 200 carrying anything else arrives here
    // looking like a good payload. Keep the last known-good basket instead - VUE-7.
    if (! isBasketPayload(data)) {
        reportUnexpectedPayload(response);

        return;
    }

    state.basket = data;

    state.basketExpiresAtMs = data.hasExpiry
        ? Date.now() + (data.expiresInSeconds ?? 0) * 1000
        : null;
}

// The two keys every consumer iterates, and the two the app sends on every path.
function isBasketPayload(data) {
    return data !== null
        && typeof data === 'object'
        && Array.isArray(data.items)
        && Array.isArray(data.vouchers);
}

// Status, content type and the start of the body are enough to name whatever sent it.
function reportUnexpectedPayload(response) {
    const { data } = response;
    const body = typeof data === 'string' ? data : JSON.stringify(data);

    Sentry.captureException(new Error('Basket endpoint returned a response that is not a basket'), {
        extra: {
            url: response.config?.url,
            status: response.status,
            contentType: response.headers?.['content-type'],
            type: Object.prototype.toString.call(data),
            body: body?.slice(0, 500),
        },
    });
}

// How long the hold has left as of right now. A function and not a computed: its answer
// changes with the clock, and nothing reactive changes with it, so a computed would cache
// the first answer and never recalculate.
export function basketExpiresInSeconds() {
    if (state.basketExpiresAtMs === null) {
        return 0;
    }

    return Math.max(0, Math.ceil((state.basketExpiresAtMs - Date.now()) / 1000));
}

export async function fetchBasket() {
    try {
        const response = await axios.get(basketApiUrl);
        setBasket(response);
    } catch (error) {
        // Both callers fire and walk away, so there is no error state to set - the button
        // keeps the count the server rendered. Reported so a real outage still shows up.
        captureUnlessPageLeft(error, { request: 'fetchBasket' });
    }
}

// Returns true/false rather than throwing - callers use the result to decide whether to
// proceed (e.g. swap the panel over to the basket).
export async function addToBasket(payload) {
    state.basketError = null;
    state.basketNotice = null;

    try {
        const response = await axios.post(basketApiUrl, payload);
        setBasket(response);
        return true;
    } catch (e) {
        state.basketError = e.response?.data?.message ?? 'Something went wrong - please try again.';
        return false;
    }
}

// Changes what an existing booking is for - its spa, date, guest count or room. Same
// true/false contract and error handling as addToBasket(), because the same panel calls
// both and its Continue button cannot care which it was.
//
// PATCHes the item's own URL, appended to the basket url exactly as removeFromBasket() does,
// so no extra endpoint needed configuring. `notice` rides in on the refreshed basket payload:
// the change succeeded, but it may have cost the customer their discount code.
// See docs/feature-requests/checkout-location-date-editing-scope-technical.md §9.
export async function updateBasketItem(guid, payload) {
    state.basketError = null;
    state.basketNotice = null;

    try {
        const response = await axios.patch(`${basketApiUrl}/${guid}`, payload);
        setBasket(response);
        state.basketNotice = response.data?.notice ?? null;
        return true;
    } catch (e) {
        state.basketError = e.response?.data?.message ?? 'Something went wrong - please try again.';
        return false;
    }
}

// Everything CalendarBookingPanel needs to reopen against an existing basket item: the
// product payload, its lookup urls, the location list and the item's current selections.
// Fetched only when somebody picks an edit control, because building it costs a price lookup
// per family member against a very large table - see BasketController::bookingPanel().
//
// Returns null rather than throwing, and sets basketError, so a caller can decline to open
// a panel it has no data for.
export async function fetchBookingPanel(guid) {
    state.basketError = null;

    try {
        const { data } = await axios.get(`${basketApiUrl}/${guid}/booking-panel`);
        return data;
    } catch (e) {
        state.basketError = e.response?.data?.message
            ?? 'Sorry, we could not open that booking to change it. Please try again.';
        return null;
    }
}

// Same shape/error-handling as addToBasket(), but posts to the gift-specific endpoint
// (buy-as-a-gift-scope-technical.md §10) since it builds a GiftVoucher shell server-side
// rather than pointing the basket item straight at the product.
export async function addGiftToBasket(payload) {
    state.basketError = null;

    try {
        const response = await axios.post(basketGiftApiUrl, payload);
        setBasket(response);
        return true;
    } catch (e) {
        state.basketError = e.response?.data?.message ?? 'Something went wrong - please try again.';
        return false;
    }
}

// A gift voucher bought for a chosen value rather than against a product, from the Monetary
// Voucher Form section type. Same shape/error-handling as the two above. `quantity` buys that
// many separate vouchers, each its own basket item - see BasketController@storeMonetaryVoucher.
export async function addMonetaryVoucherToBasket(payload) {
    state.basketError = null;

    try {
        const response = await axios.post(basketMonetaryVoucherApiUrl, payload);
        setBasket(response);
        return true;
    } catch (e) {
        state.basketError = e.response?.data?.message ?? 'Something went wrong - please try again.';
        return false;
    }
}

// Both removals surface their failure the way addToBasket() does rather than rejecting.
// The 409 editability gate is the case that matters: once payment is being processed the
// server refuses, and without this the click would simply do nothing and the reason would
// be thrown away.
export async function removeFromBasket(guid) {
    await remove(`${basketApiUrl}/${guid}`);
}

// Takes the voucher off the order, and with it whatever it was paying for - the server
// removes the linked item too, because that line was priced from the voucher and there is
// no catalogue price to put back. No confirmation step by decision; the row says what it
// covers so the consequence is on screen before the click.
export async function removeVoucherFromBasket(id) {
    await remove(`${basketVoucherApiUrl}/${id}`);
}

async function remove(url) {
    state.basketError = null;
    state.basketNotice = null;

    try {
        const response = await axios.delete(url);
        setBasket(response);
    } catch (e) {
        // abort_if()'s own message for the 409, so the customer is told the order is
        // already being processed rather than given a generic failure.
        state.basketError = e.response?.data?.message ?? 'Something went wrong - please try again.';
    }
}
